// src/writer/templates/bookTemplates.ts
import { BookTemplate, TemplateCategory } from '../types';

// Fiction Novel Template
const fictionNovelTemplate: BookTemplate = {
    id: 'fiction-novel',
    name: 'Fiction Novel',
    genre: 'Fiction',
    description: 'Standard fiction novel with traditional front and back matter structure',
    fictionVsNonFiction: 'Fiction',
    frontMatterDefaults: {
        dedication: '',
        epigraph: '',
        prologue: '',
        acknowledgmentsFront: '',
        tableOfContents: ''
    },
    backMatterDefaults: {
        epilogue: '',
        acknowledgmentsBack: '',
        aboutTheAuthor: '',
        readingGroupGuide: '',
        teaserNextBook: ''
    },
    publishingDefaults: {
        mediumFormat: 'both',
        marketingBlurb: '',
        coverDesignBrief: ''
    },
    bookMetadataDefaults: {
        fictionVsNonFiction: 'Fiction',
        genreSubject: 'Novel',
        narrativePointOfView: 'Third Person',
        tense: 'Past',
        structure: 'Three-Act Structure',
    },
    authorDetailsDefaults: {
        authorName: '',
        authorBio: '',
        authorWebsite: '',
        authorSocialMedia: '',
        authorContact: ''
    },
    targetWordCount: 80000,
    suggestedStructure: 'Three-act structure with clear beginning, middle, and end. Include character development arcs and plot progression.',
    commonElements: ['Title Page', 'Copyright Page', 'Dedication', 'Prologue', 'Main Story', 'Epilogue', 'About the Author'],
    professionalTips: [
        'Include ISBN and copyright information',
        'Keep dedication brief and meaningful',
        'Prologue should be essential to the story',
        'About the Author builds reader connection'
    ]
};

// Non-Fiction Book Template
const nonFictionTemplate: BookTemplate = {
    id: 'non-fiction',
    name: 'Non-Fiction Book',
    genre: 'Non-Fiction',
    description: 'Professional non-fiction book with academic and reference elements',
    fictionVsNonFiction: 'Non-fiction',
    frontMatterDefaults: {
        dedication: '',
        foreword: '',
        preface: '',
        acknowledgmentsFront: '',
        introduction: '',
        tableOfContents: ''
    },
    backMatterDefaults: {
        afterword: '',
        acknowledgmentsBack: '',
        aboutTheAuthor: '',
        bibliography: [],
        glossary: [],
        index: '',
        appendices: []
    },
    publishingDefaults: {
        mediumFormat: 'both',
        marketingBlurb: '',
        coverDesignBrief: '',
        copyrightRegistrationNotes: ''
    },
    bookMetadataDefaults: {
        fictionVsNonFiction: 'Non-fiction',
        genreSubject: 'Informational',
        structure: 'Logical/Thematic',
        scholarlyApparatus: 'Bibliography, Index, Footnotes',
    },
    authorDetailsDefaults: {
        authorName: '',
        authorBio: '',
        authorWebsite: '',
        authorSocialMedia: '',
        authorContact: ''
    },
    targetWordCount: 60000,
    suggestedStructure: 'Logical progression with clear introduction, supporting chapters, and conclusion. Include references and citations.',
    commonElements: ['Title Page', 'Copyright Page', 'Table of Contents', 'Introduction', 'Main Content', 'Bibliography', 'Index', 'About the Author'],
    professionalTips: [
        'Include comprehensive bibliography and sources',
        'Add glossary for technical terms',
        'Index is essential for reference books',
        'Preface explains your motivation and approach'
    ]
};

// Memoir/Biography Template
const memoirTemplate: BookTemplate = {
    id: 'memoir-biography',
    name: 'Memoir/Biography',
    genre: 'Memoir',
    description: 'Personal narrative with chronological or thematic structure',
    fictionVsNonFiction: 'Non-fiction',
    frontMatterDefaults: {
        dedication: '',
        foreword: '',
        preface: '',
        acknowledgmentsFront: '',
        prologue: '',
        tableOfContents: ''
    },
    backMatterDefaults: {
        epilogue: '',
        afterword: '',
        acknowledgmentsBack: '',
        aboutTheAuthor: '',
        bibliography: []
    },
    publishingDefaults: {
        mediumFormat: 'both',
        marketingBlurb: '',
        coverDesignBrief: ''
    },
    bookMetadataDefaults: {
        fictionVsNonFiction: 'Non-fiction',
        genreSubject: 'Memoir/Biography',
        narrativePointOfView: 'First Person',
        tense: 'Past',
        structure: 'Chronological or Thematic',
    },
    authorDetailsDefaults: {
        authorName: '',
        authorBio: '',
        authorWebsite: '',
        authorSocialMedia: '',
        authorContact: ''
    },
    targetWordCount: 70000,
    suggestedStructure: 'Chronological timeline or thematic chapters focusing on key life events and their significance.',
    commonElements: ['Title Page', 'Copyright Page', 'Dedication', 'Foreword', 'Main Story', 'Epilogue', 'Acknowledgments', 'About the Author'],
    professionalTips: [
        'Focus on universal themes readers can relate to',
        'Include photos if available and relevant',
        'Verify facts and get permissions for mentioned persons',
        'Balance personal details with broader significance'
    ]
};

// Children's Book Template
const childrensBookTemplate: BookTemplate = {
    id: 'childrens-book',
    name: "Children's Book",
    genre: "Children's Literature",
    description: 'Age-appropriate content with simplified structure and engaging elements',
    fictionVsNonFiction: 'Fiction',
    frontMatterDefaults: {
        dedication: '',
        tableOfContents: ''
    },
    backMatterDefaults: {
        aboutTheAuthor: '',
        readingGroupGuide: ''
    },
    publishingDefaults: {
        mediumFormat: 'PDF',
        marketingBlurb: '',
        coverDesignBrief: 'Colorful, engaging, age-appropriate illustration'
    },
    bookMetadataDefaults: {
        fictionVsNonFiction: 'Fiction',
        genreSubject: "Children's Story",
        audienceAgeBand: '5-12 years',
        narrativePointOfView: 'Third Person',
        tense: 'Past',
        structure: 'Simple narrative arc',
    },
    authorDetailsDefaults: {
        authorName: '',
        authorBio: '',
        authorWebsite: '',
        authorSocialMedia: '',
        authorContact: ''
    },
    targetWordCount: 2000,
    suggestedStructure: 'Simple beginning-middle-end structure with clear moral or lesson. Age-appropriate vocabulary and themes.',
    commonElements: ['Title Page', 'Copyright Page', 'Main Story', 'About the Author'],
    professionalTips: [
        'Keep vocabulary appropriate for target age',
        'Include moral or educational value',
        'Consider illustration placement',
        'Test readability with target audience'
    ]
};

// Poetry Collection Template
const poetryTemplate: BookTemplate = {
    id: 'poetry-collection',
    name: 'Poetry Collection',
    genre: 'Poetry',
    description: 'Curated collection of poems with thematic or chronological organization',
    fictionVsNonFiction: 'Fiction',
    frontMatterDefaults: {
        dedication: '',
        epigraph: '',
        foreword: '',
        acknowledgmentsFront: '',
        tableOfContents: ''
    },
    backMatterDefaults: {
        afterword: '',
        acknowledgmentsBack: '',
        aboutTheAuthor: ''
    },
    publishingDefaults: {
        mediumFormat: 'both',
        marketingBlurb: '',
        coverDesignBrief: 'Artistic, minimalist design reflecting poetic themes'
    },
    bookMetadataDefaults: {
        fictionVsNonFiction: 'Fiction',
        genreSubject: 'Poetry',
        structure: 'Thematic Sections',
    },
    authorDetailsDefaults: {
        authorName: '',
        authorBio: '',
        authorWebsite: '',
        authorSocialMedia: '',
        authorContact: ''
    },
    targetWordCount: 15000,
    suggestedStructure: 'Organize poems thematically or chronologically. Consider progression and flow between pieces.',
    commonElements: ['Title Page', 'Copyright Page', 'Table of Contents', 'Poetry Sections', 'About the Author'],
    professionalTips: [
        'Group poems by theme or style',
        'Consider white space and formatting',
        'Include publication credits if applicable',
        'Table of contents with first lines helpful'
    ]
};

// Short Story Collection Template
const shortStoryTemplate: BookTemplate = {
    id: 'short-story-collection',
    name: 'Short Story Collection',
    genre: 'Short Stories',
    description: 'Collection of short stories with connecting themes or standalone pieces',
    fictionVsNonFiction: 'Fiction',
    frontMatterDefaults: {
        dedication: '',
        foreword: '',
        acknowledgmentsFront: '',
        tableOfContents: ''
    },
    backMatterDefaults: {
        afterword: '',
        acknowledgmentsBack: '',
        aboutTheAuthor: '',
        readingGroupGuide: ''
    },
    publishingDefaults: {
        mediumFormat: 'both',
        marketingBlurb: '',
        coverDesignBrief: ''
    },
    bookMetadataDefaults: {
        fictionVsNonFiction: 'Fiction',
        genreSubject: 'Short Story Collection',
        narrativePointOfView: 'Varied',
        structure: 'Episodic',
    },
    authorDetailsDefaults: {
        authorName: '',
        authorBio: '',
        authorWebsite: '',
        authorSocialMedia: '',
        authorContact: ''
    },
    targetWordCount: 40000,
    suggestedStructure: 'Arrange stories for flow and pacing. Consider thematic connections or contrasts between pieces.',
    commonElements: ['Title Page', 'Copyright Page', 'Table of Contents', 'Individual Stories', 'About the Author'],
    professionalTips: [
        'Arrange stories for optimal reading flow',
        'Consider opening and closing pieces carefully',
        'Include publication credits if stories were published',
        'Strong opening story sets tone for collection'
    ]
};

// Template Categories
export const templateCategories: TemplateCategory[] = [
    {
        id: 'fiction',
        name: 'Fiction',
        description: 'Templates for fictional works including novels and stories',
        templates: [fictionNovelTemplate, childrensBookTemplate, poetryTemplate, shortStoryTemplate]
    },
    {
        id: 'non-fiction',
        name: 'Non-Fiction',
        description: 'Templates for factual and informational works',
        templates: [nonFictionTemplate, memoirTemplate]
    }
];

// All templates flattened
export const allBookTemplates: BookTemplate[] = [
    fictionNovelTemplate,
    nonFictionTemplate,
    memoirTemplate,
    childrensBookTemplate,
    poetryTemplate,
    shortStoryTemplate
];

// Helper function to get template by ID
export const getTemplateById = (id: string): BookTemplate | undefined => {
    return allBookTemplates.find(template => template.id === id);
};

// Helper function to apply template to project state
export const applyTemplateToProject = (template: BookTemplate) => {
    return {
        bookMetadata: { ...template.bookMetadataDefaults },
        authorDetails: { ...(template as any).authorDetailsDefaults },
        frontMatter: { 
            content: '',
            titlePageContent: '',
            titlePageImageBase64: null,
            copyrightPageContent: '',
            forewordBy: '',
            ...template.frontMatterDefaults 
        },
        backMatter: { 
            content: '',
            backCoverImageBase64: null,
            contactSocialMedia: '',
            ...template.backMatterDefaults 
        },
        publishingEssentials: { 
            metadataFileNotes: '',
            queryLetterDraft: '',
            isbnNotes: '',
            copyrightRegistrationNotes: '',
            ...template.publishingDefaults 
        }
    };
};